// Shelf Drop filter row.
//
// Visual styling mirrors the Pick Queue selector (PickTypeSelector.tsx) —
// two stacked segmented-control rows using `.picking-filter`, with a
// "Custom" date-range popover anchored to the trailing pill. Keeps the
// experience consistent for pickers who flip between Pick Queue and
// Shelf Drop on the same handheld.
//
// Three independent inputs:
//   - channel:    All | ⚡ Express | 🚚 Standard
//   - age bucket: All | 2+ | 1-2 | <1 | 📅 Custom
//   - custom range: optional date-from / date-to, each with an
//     optional time-of-day (only used when the user opens the
//     Custom popover). A blank time means start-of-day for the
//     From bound and end-of-day for the To bound, so date-only
//     use keeps whole-day semantics.
//   - due ("must ship today" clock): on/off toggle that stacks with
//     the other filters. Same rule as the Pick Queue selector — it
//     only renders (and only takes effect) after the 13:00 London
//     dispatch cut-off, because before then everything in the queue
//     is still today's work and the filter has nothing to separate.
import { useEffect, useRef, useState } from "react";
import { Zap, Truck, CalendarRange, Clock } from "lucide-react";
import { useTranslation } from "../../i18n";
import { isAfterDispatchCutoff } from "../helpers/dispatchDue";

export type DropChannel = "all" | "express" | "standard";
export type DropAge = "all" | "<1" | "1-2" | "2+" | "custom";

export interface DropFilters {
    channel: DropChannel;
    age: DropAge;
    /** YYYY-MM-DD or empty. Only meaningful when age === "custom". */
    ageFrom?: string;
    /** YYYY-MM-DD or empty. Only meaningful when age === "custom". */
    ageTo?: string;
    /** HH:mm or empty — time-of-day for ageFrom (defaults 00:00). */
    ageFromTime?: string;
    /** HH:mm or empty — time-of-day for ageTo (defaults 23:59). */
    ageToTime?: string;
    /**
     * "Must ship today" clock toggle — the picker's stored choice. Only
     * applied (see dropWireFilters) while the toggle is visible, i.e.
     * after the 13:00 London cut-off, so a choice left on overnight
     * can't silently narrow the next morning's queue.
     */
    due?: boolean;
}

/**
 * The filter shape sent to the BE (/drop/next query params and the
 * /drop/pick-complete body). The custom bounds are composed into full
 * ISO timestamps here — the handheld's clock is warehouse-local, so
 * `new Date("YYYY-MM-DDTHH:mm")` (parsed as local time) then
 * `.toISOString()` yields the correct instant regardless of the BE
 * server's timezone. Times are optional: a missing From time means
 * start-of-day, a missing To time means end-of-day (inclusive).
 */
export const dropWireFilters = (f: DropFilters, nowMs: number = Date.now(), cutoff?: string | null): {
    channel: DropChannel;
    age: DropAge;
    ageFrom?: string;
    ageTo?: string;
    due?: true;
} => {
    const wire: { channel: DropChannel; age: DropAge; ageFrom?: string; ageTo?: string; due?: true } = {
        channel: f.channel,
        age: f.age,
    };
    // The clock only counts after the cut-off; before it the stored
    // choice is inert (and the toggle isn't rendered). Omitted when off
    // so the wire shape — and every BE cache key — is unchanged for
    // pickers who never touch it.
    if (f.due === true && isAfterDispatchCutoff(nowMs, cutoff)) wire.due = true;
    if (f.age !== "custom") return wire;
    const compose = (date: string | undefined, time: string | undefined, fallback: string) => {
        if (!date) return undefined;
        const d = new Date(`${date}T${time || fallback}`);
        return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
    };
    const from = compose(f.ageFrom, f.ageFromTime, "00:00:00");
    const to   = compose(f.ageTo,   f.ageToTime ? `${f.ageToTime}:59.999` : undefined, "23:59:59.999");
    if (from) wire.ageFrom = from;
    if (to)   wire.ageTo   = to;
    return wire;
};

interface Props {
    value: DropFilters;
    onChange: (next: DropFilters) => void;
    /** Render the "must ship today" clock toggle (after the 13:00 cut-off). */
    showDue?: boolean;
}

const DELIVERY_SEGMENTS: DropChannel[] = ["all", "express", "standard"];
const AGE_SEGMENTS: DropAge[] = ["all", "2+", "1-2", "<1", "custom"];

const DropFilterPills = ({ value, onChange, showDue = false }: Props) => {
    const { t } = useTranslation();
    const [customOpen, setCustomOpen] = useState(false);

    // Close the Custom date popover when the user clicks outside of
    // its wrapper. Same behaviour as PickTypeSelector.
    const customRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!customOpen) return;
        const handler = (e: MouseEvent | TouchEvent) => {
            if (!customRef.current) return;
            if (customRef.current.contains(e.target as Node)) return;
            setCustomOpen(false);
        };
        window.addEventListener("mousedown", handler);
        window.addEventListener("touchstart", handler);
        return () => {
            window.removeEventListener("mousedown", handler);
            window.removeEventListener("touchstart", handler);
        };
    }, [customOpen]);

    const setChannel = (next: DropChannel) => onChange({ ...value, channel: next });
    const toggleDue = () => onChange({ ...value, due: !value.due });
    const setAge = (next: DropAge) => {
        onChange({ ...value, age: next });
        if (next === "custom") setCustomOpen(true);
    };
    const setAgeFrom = (next: string) => onChange({ ...value, ageFrom: next || undefined });
    const setAgeTo = (next: string) => onChange({ ...value, ageTo: next || undefined });
    const setAgeFromTime = (next: string) => onChange({ ...value, ageFromTime: next || undefined });
    const setAgeToTime = (next: string) => onChange({ ...value, ageToTime: next || undefined });
    const clearCustomRange = () => onChange({
        ...value,
        ageFrom: undefined,
        ageTo: undefined,
        ageFromTime: undefined,
        ageToTime: undefined,
    });
    const customRangeEmpty =
        !value.ageFrom && !value.ageTo && !value.ageFromTime && !value.ageToTime;

    const channelLabel = (seg: DropChannel) =>
        seg === "all" ? t("picking_filter_all")
        : seg === "express" ? t("picking_filter_express_label")
        : t("picking_filter_standard_label");

    const ageLabel = (seg: DropAge) => {
        switch (seg) {
            case "all":    return t("picking_filter_all");
            case "<1":     return t("drop_filter_age_lt1");
            case "1-2":    return t("drop_filter_age_1to2");
            case "2+":     return t("drop_filter_age_2plus");
            case "custom": return t("picking_filter_custom");
        }
    };

    return (
        <>
            <div className="picking-filter-row">
                <div className="picking-filter picking-filter--delivery" role="tablist" aria-label={t("picking_filter_delivery_aria")}>
                    {DELIVERY_SEGMENTS.map(seg => {
                        const active = value.channel === seg;
                        const icon = seg === "express"  ? <Zap   size={16} fill="currentColor" stroke="none" aria-hidden="true" />
                                  : seg === "standard" ? <Truck size={16} strokeWidth={2} aria-hidden="true" />
                                  : null;
                        const label = channelLabel(seg);
                        return (
                            <button
                                key={seg}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                className={`picking-filter-btn${active ? " picking-filter-btn--on" : ""}${icon ? " picking-filter-btn--icon" : ""}`}
                                onClick={() => setChannel(seg)}
                                title={label}
                                aria-label={label}
                            >
                                <span className="picking-filter-label">{icon ?? label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* "Must ship today" clock toggle — same control as the
                    Pick Queue selector, minus the count badge: the units
                    badge at the top of the page already honours every
                    applied filter, this one included. */}
                {showDue && (
                    <div className="picking-filter picking-filter--due">
                        <button
                            type="button"
                            aria-pressed={value.due === true}
                            className={`picking-filter-btn picking-filter-btn--icon picking-filter-btn--due${value.due ? " picking-filter-btn--on" : ""}`}
                            onClick={toggleDue}
                            title={t("picking_filter_due_today")}
                            aria-label={t("picking_filter_due_today")}
                        >
                            <span className="picking-filter-label">
                                <Clock size={16} strokeWidth={2.2} aria-hidden="true" />
                            </span>
                        </button>
                    </div>
                )}
            </div>

            <div className="picking-filter picking-filter--age" role="tablist" aria-label={t("picking_filter_age_aria")}>
                {AGE_SEGMENTS.map(seg => {
                    const active = value.age === seg;
                    const label = ageLabel(seg);
                    if (seg === "custom") {
                        return (
                            <div key={seg} className="picking-filter-custom-wrapper" ref={customRef}>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    className={`picking-filter-btn picking-filter-btn--icon picking-filter-btn--custom${active ? " picking-filter-btn--on" : ""}`}
                                    onClick={() => {
                                        if (active) setCustomOpen(o => !o);
                                        else setAge("custom");
                                    }}
                                    title={label}
                                    aria-label={label}
                                    aria-expanded={customOpen}
                                >
                                    <span className="picking-filter-label">
                                        <CalendarRange size={16} strokeWidth={2} aria-hidden="true" />
                                    </span>
                                </button>
                                {customOpen && (
                                    <div className="picking-custom-popover" role="dialog" aria-label={t("picking_filter_custom")}>
                                        <div className="picking-custom-field">
                                            <span className="picking-custom-field-label">{t("picking_filter_custom_from")}</span>
                                            <div className="picking-custom-datetime">
                                                <input
                                                    type="date"
                                                    value={value.ageFrom ?? ""}
                                                    onChange={e => setAgeFrom(e.target.value)}
                                                    className="picking-custom-input picking-custom-input--date"
                                                    aria-label={t("picking_filter_custom_from")}
                                                />
                                                <input
                                                    type="time"
                                                    value={value.ageFromTime ?? ""}
                                                    onChange={e => setAgeFromTime(e.target.value)}
                                                    className="picking-custom-input picking-custom-input--time"
                                                    aria-label={t("picking_filter_custom_from_time")}
                                                    title={t("picking_filter_custom_from_time")}
                                                />
                                            </div>
                                        </div>
                                        <div className="picking-custom-field">
                                            <span className="picking-custom-field-label">{t("picking_filter_custom_to")}</span>
                                            <div className="picking-custom-datetime">
                                                <input
                                                    type="date"
                                                    value={value.ageTo ?? ""}
                                                    onChange={e => setAgeTo(e.target.value)}
                                                    className="picking-custom-input picking-custom-input--date"
                                                    aria-label={t("picking_filter_custom_to")}
                                                />
                                                <input
                                                    type="time"
                                                    value={value.ageToTime ?? ""}
                                                    onChange={e => setAgeToTime(e.target.value)}
                                                    className="picking-custom-input picking-custom-input--time"
                                                    aria-label={t("picking_filter_custom_to_time")}
                                                    title={t("picking_filter_custom_to_time")}
                                                />
                                            </div>
                                        </div>
                                        <div className="picking-custom-actions">
                                            <button
                                                type="button"
                                                className="picking-custom-clear"
                                                onClick={clearCustomRange}
                                                disabled={customRangeEmpty}
                                            >
                                                {t("picking_filter_custom_clear")}
                                            </button>
                                            <button
                                                type="button"
                                                className="picking-custom-done"
                                                onClick={() => setCustomOpen(false)}
                                            >
                                                {t("picking_filter_custom_done")}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    }
                    return (
                        <button
                            key={seg}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            className={`picking-filter-btn${active ? " picking-filter-btn--on" : ""}`}
                            onClick={() => setAge(seg)}
                            title={label}
                            aria-label={label}
                        >
                            <span className="picking-filter-label">{label}</span>
                        </button>
                    );
                })}
            </div>
        </>
    );
};

export default DropFilterPills;
